import _ from 'lodash';
import moment from "moment";
import {changedKeys} from "util/ObjectUtil";
import {getSisDateTimeAsString} from 'util/SisDateTime';
import {BSAttr} from 'module/BSEntry/BSAttr';
import {PersonAttr} from 'module/selpan/impl/person/model/PersonUtil';
import {SisName} from 'util/SisName';

export const DTLockType = {
	BookingEnd: 'bookingEnd',
	Approval: 'approval',
};

const getLocksForBSModel = (model, entryDesc, approval, bookingEnd) =>
	getDTLocksFor(model.sisName, BSAttr.getStartDateTime(model), BSAttr.getEndDateTime(model), entryDesc, approval, bookingEnd);


const getErrorMsgsForLimitsPerKey = (changedKeys, {limit, limitType} = {}) => {
	if (!limit) return; // no locks present
	const message = getDTLocksMessage(limit, limitType);
	return changedKeys.map(field => ({field, message}));
};

const validateBookingLimits = (persistedModel, model, entryDescById, approval, bookingEnd) => {
	// when persisted model is locked then there are no changes allowed.
	// new models must be created on valid date
	model = persistedModel || model;
	const result = getLocksForBSModel(model, entryDescById[model._edId], approval, bookingEnd);

	if (result.lockedStart) {
		return getErrorMsgsForLimitsPerKey(['bookingDate', 'bookingTime'], result);
	}
	return null;
};

const persurlHardLockedFields = ['persNr', 'start', 'hourCategory', '_edId'];
const getPersurlErrorFields = (locks) => _.keys(_.pickBy({start: locks.lockedStart, end: locks.lockedEnd}));
const validatePersurlLimits = (persistedModel, model, entryDescById, approval, bookingEnd) => {
	//// 4 possible cases for persurls
	// #0. new/modified end must not be lower than any limit
	// #1. new -> no limit above start/end
	// #2. old end before limit -> locked
	// #3. old end after limit -> end can get cropped to limit, changes possible (but NOT _edId, hourCategory, start)
	// #4. old start after limit -> new must be in limits
	const mod = getLocksForBSModel(model, entryDescById[model._edId], approval, bookingEnd);
	if (!persistedModel || mod.lockedEnd) {
		if (mod.lockedStart || mod.lockedEnd) {
			// #0. #1.
			return getErrorMsgsForLimitsPerKey(getPersurlErrorFields(mod), mod);
		}
	} else {
		// #2. #3. #4.
		const old = getLocksForBSModel(persistedModel, entryDescById[persistedModel._edId], approval, bookingEnd);
		if (old.lockedEnd) {
			// #2.
			return getErrorMsgsForLimitsPerKey(getPersurlErrorFields(old), mod);
		} else if (old.lockedStart) {
			const lockedFieldNames = changedKeys(
				_.pick(model, persurlHardLockedFields),
				_.pick(persistedModel, persurlHardLockedFields),
			);
			if (lockedFieldNames.length) {
				// #3.
				return getErrorMsgsForLimitsPerKey(getPersurlErrorFields(old), mod);
			}// else -> other field changes are allowed
		} else {
			if (mod.lockedStart) {
				// #4.
				return getErrorMsgsForLimitsPerKey(getPersurlErrorFields(mod), mod);
			}
		}
	}
	// no error
	return null;
};

export const getDTLocksFor = (sisName, startDateTime, endDateTime, entryDesc, approval, bookingEnd,
) => {
	if (entryDesc.bookingEndNoCheck) {
		bookingEnd = null;
	}
	if (entryDesc.approvalNoCheck) {
		approval = null;
	}
	const maxLimit = bookingEnd && approval && moment.max(approval, bookingEnd) || bookingEnd || approval;
	const ret = {
		lockedStart: true,
		lockedEnd: true,
		limit: maxLimit,
		limitType: bookingEnd === maxLimit && DTLockType.BookingEnd || approval === maxLimit && DTLockType.Approval || null,
	};
	if (!maxLimit) {
		ret.lockedStart = false;
		ret.lockedEnd = false;
		return ret;
	}

	// bookings only use startDateTime, because it would change results of already locked VerrLauf results.
	if (startDateTime && startDateTime.isAfter(maxLimit)) {
		ret.lockedStart = false;
	}
	if (sisName === SisName.Booking) {
		ret.lockedEnd = ret.lockedStart;
	} else {
		if (endDateTime && endDateTime.isAfter(maxLimit)) {
			ret.lockedEnd = false;
		}
	}
	return ret;
};

export const getDTLocksMessage = (limit, limitType) => {
	if (!limit) return null;
	let errMsgPrefix;
	switch (limitType) {
		case DTLockType.BookingEnd:
			errMsgPrefix = 'Der Buchungsschluss';
			break;
		case DTLockType.Approval:
			errMsgPrefix = 'Die Freigabe';
			break;
		default:
			errMsgPrefix = null;
	}
	return `${errMsgPrefix} verhindert diese Aktion (${getSisDateTimeAsString(limit)}).`;
};

export const validateDateLimits = (persistedModel, model, entryDescById, personsById) => {
	const {sisName} = model;
	const person = personsById[model.persNr];
	let approval = PersonAttr.getApprovalDateTime(person);
	let bookingEnd = PersonAttr.getBookingEndDateTime(person);
	if (persistedModel && model.persNr !== persistedModel.persNr) {
		const oldPers = personsById[persistedModel.persNr];
		approval = moment.max(approval, PersonAttr.getApprovalDateTime(oldPers));
		bookingEnd = moment.max(bookingEnd, PersonAttr.getBookingEndDateTime(oldPers));
	}
	switch (sisName) {
		case SisName.Booking:
			return validateBookingLimits(persistedModel, model, entryDescById, approval, bookingEnd);
		case SisName.Persurl:
			return validatePersurlLimits(persistedModel, model, entryDescById, approval, bookingEnd);
		case SisName.PlanTempl:
			return false
	}
	return [{message: 'invalid model'}];
};